/**
 * An event that waits on itself (D159, from Calder's D158 review).
 *
 * @remarks
 * `event_pending` asks whether an occurrence of an event is still on the
 * scheduler's queue. Asked from INSIDE that event's own `conditions`, it
 * reads oddly: the scheduler consumes the due occurrence BEFORE it judges
 * the conditions (`scheduler-tick.ts` — processed either way, fired only if
 * met), so by the time the question is put, the occurrence being judged is
 * already gone. The read is true only when ANOTHER occurrence of the same
 * event is queued — scheduled twice from elsewhere, or by an earlier firing
 * of itself — and false in every ordinary case.
 *
 * That is not constant-false, so it is not refused: "fire only while
 * another of me is still coming" is strange, but it is a thing a pack can
 * mean. It is a WARNING, in D141's baseline — accept it with the reason if
 * it is meant; and if it is not, what was probably meant is the event's
 * own `conditions` gating on the world, or `event_fired` on the previous
 * occurrence.
 *
 * The reference walker cannot see this: `checkCondition` walks a condition
 * without knowing which event owns it. This module walks the events, where
 * the owner is known.
 *
 * @packageDocumentation
 */

import type { ConditionDefinition, ContentPackDefinition } from '@orpheus/content-schema';

/** One event whose own conditions ask whether it is still pending. */
export interface EventWaitsOnItself {
	readonly eventId: string;
	/** The condition's path in the document — `events[2].conditions[0].conditions[1]`. */
	readonly path: string;
}

/** Every `event_pending` leaf under one condition that names the given event, with its path. */
const selfReferencesIn = (condition: ConditionDefinition, eventId: string, path: string): readonly string[] => {
	switch (condition.type) {
		case 'all_of':
		case 'any_of':
		case 'at_least_of': {
			return condition.conditions.flatMap((nested, index) => selfReferencesIn(nested, eventId, `${path}.conditions[${String(index)}]`));
		}
		case 'event_pending': {
			return condition.eventId === eventId ? [path] : [];
		}
		case 'not': {
			return selfReferencesIn(condition.condition, eventId, `${path}.condition`);
		}
		default: {
			return [];
		}
	}
};

/**
 * Every event whose own conditions ask whether it is still pending, in
 * document order.
 *
 * @param contentPack - The parsed pack
 * @returns The self-references, with their paths
 */
export const findEventsWaitingOnThemselves = (contentPack: ContentPackDefinition): readonly EventWaitsOnItself[] =>
	contentPack.events.flatMap((event, eventIndex) =>
		event.conditions.flatMap((condition, conditionIndex) =>
			selfReferencesIn(condition, event.eventId, `events[${String(eventIndex)}].conditions[${String(conditionIndex)}]`).map(
				(path): EventWaitsOnItself => ({ eventId: event.eventId, path }),
			),
		),
	);
